import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Updates package contents list when the variant changes.
 *
 * @typedef {Object} Refs
 * @property {HTMLUListElement} list
 *
 * @extends {Component<Refs>}
 */
class ProductPackageContents extends Component {
  /** @type {Record<string, string[]> | null} */
  #packageByVariant = null;

  connectedCallback() {
    super.connectedCallback();
    this.#packageByVariant = this.#parsePackageByVariant();

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    target?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #parsePackageByVariant() {
    if (!this.dataset.packageByVariant) return null;

    try {
      return JSON.parse(this.dataset.packageByVariant);
    } catch {
      return null;
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail?.resource;
    if (!variant?.id || !this.refs.list || !this.#packageByVariant) return;

    const lines = this.#packageByVariant[String(variant.id)];
    if (!lines?.length) return;

    this.refs.list.innerHTML = lines
      .map((line) => {
        const item = String(line).trim();
        if (!item) return '';
        return `<li>${this.#escapeHtml(item)}</li>`;
      })
      .filter(Boolean)
      .join('');
  };

  /**
   * @param {string} text
   * @returns {string}
   */
  #escapeHtml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('product-package-contents')) {
  customElements.define('product-package-contents', ProductPackageContents);
}
